import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [paginationDots] - The pagination dots container.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Initialize pagination dots
    this.#initializePaginationDots();

    // Initialize thumbnail scroll arrows with a delay
    setTimeout(() => this.#initializeThumbnailScrollArrows(), 150);

    // Listen to slideshow changes to update dots
    this.refs.slideshow?.addEventListener('slideshow:select', this.#handleSlideshowSelect, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles slideshow selection changes to update pagination dots.
   * @param {CustomEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    const index = event.detail.index;
    this.#updateActiveDot(index);
  };

  /**
   * Initializes pagination dots functionality.
   */
  #initializePaginationDots() {
    if (!this.refs.paginationDots) return;

    const dots = this.refs.paginationDots.querySelectorAll('.pagination-dot');
    
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index, 10);
        this.refs.slideshow?.select(index);
      });
    });
  }

  /**
   * Updates the active state of pagination dots.
   * @param {number} activeIndex - The index of the active slide.
   */
  #updateActiveDot(activeIndex) {
    if (!this.refs.paginationDots) return;

    const dots = this.refs.paginationDots.querySelectorAll('.pagination-dot');
    
    dots.forEach((dot, index) => {
      if (index === activeIndex) {
        dot.classList.add('pagination-dot--active');
      } else {
        dot.classList.remove('pagination-dot--active');
      }
    });
  }

  /**
   * Initializes thumbnail scroll arrows functionality.
   */
  #initializeThumbnailScrollArrows() {
    const wrapper = this.querySelector('[data-thumbnail-scroll-container]');
    if (!wrapper) return;

    const slideshowControls = wrapper.querySelector('slideshow-controls');
    if (!slideshowControls) return;

    // Find the scrollable container - try multiple selectors
    let scrollContainer = slideshowControls.querySelector('.slideshow-pagination__list') ||
                          slideshowControls.querySelector('[scroll-mode="vertical"]') ||
                          slideshowControls.querySelector('[class*="pagination"]');
    
    if (!scrollContainer) return;

    const upButton = this.querySelector('[data-scroll-direction="up"]');
    const downButton = this.querySelector('[data-scroll-direction="down"]');

    if (!upButton || !downButton) return;

    const scrollAmount = 150;

    // Handle up button click
    upButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      scrollContainer.scrollBy({ top: -scrollAmount, behavior: 'smooth' });
    });

    // Handle down button click
    downButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      scrollContainer.scrollBy({ top: scrollAmount, behavior: 'smooth' });
    });

    // Update button states
    const updateButtonStates = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      upButton.disabled = scrollTop <= 5;
      downButton.disabled = scrollTop + clientHeight >= scrollHeight - 5;
    };

    setTimeout(updateButtonStates, 200);
    scrollContainer.addEventListener('scroll', updateButtonStates);
    window.addEventListener('resize', updateButtonStates);
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}